/* eslint-disable no-param-reassign */
import { Map } from 'immutable';
import qs from 'query-string';
import { useContext, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { SubscriptionModalContext } from 'components/App';
import { small } from 'constants/mediaQueries';
import { getJwt, setJwt } from 'utils/jwt';

import { MeContext, NotificationContext, PlanContext } from '../ProviderWrapper';

export function useDisableScroll(value) {
    useEffect(() => {
        const body = document.getElementsByTagName('body')[0];

        if (value) {
            body.style.overflow = 'hidden';
        } else {
            body.style.overflow = '';
        }

        return () => {
            body.style.overflow = '';
        };
    }, [value]);
}

export function useMeContext() {
    return useContext(MeContext);
}

export function useSubscriptionModalContext() {
    return useContext(SubscriptionModalContext);
}

export function useUpgradeContext() {
    return useContext(SubscriptionModalContext);
}

export function useOnClickOutside(ref, onClickOutside, shouldHandleOutsideClick = true) {
    useEffect(() => {
        function handleClick(e) {
            if (ref && ref.current.contains(e.target)) {
                return;
            }

            onClickOutside();
        }

        if (shouldHandleOutsideClick) {
            document.addEventListener('mousedown', handleClick, false);
        }

        return () => {
            document.removeEventListener('mousedown', handleClick, false);
        };
    }, [ref, onClickOutside, shouldHandleOutsideClick]);
}

export function useRefSize(ref) {
    const [size, setSize] = useState([0, 0]);

    useLayoutEffect(() => {
        function updateSize() {
            if (ref.current) {
                setSize([ref.current.clientWidth, ref.current.clientHeight]);
            }
        }
        window.addEventListener('resize', updateSize);
        updateSize();

        return () => window.removeEventListener('resize', updateSize);
    }, [ref.current]);

    return size;
}

export function useJwt() {
    const [jwt, updateJwt] = useState(getJwt());

    useEffect(() => {
        setJwt(jwt);
    }, [jwt]);

    return [jwt, updateJwt];
}

export function useSearchParams() {
    const history = useHistory();
    const { hash, pathname, search } = history.location;
    const [searchParams, setSearchParams] = useState(Map());
    function updateSearchParams(map) {
        const filteredMap = map.filter((v) => v);
        const query = filteredMap.size ? `?${qs.stringify(filteredMap.toJS())}` : '';

        history.push(`${pathname}${query}${hash}`);
    }

    useEffect(() => {
        const urlSearchParams = new URLSearchParams(search);

        setSearchParams(Map(Object.fromEntries([...urlSearchParams])));
    }, [search]);

    return [searchParams, updateSearchParams];
}

export function usePlanContext() {
    return useContext(PlanContext);
}

export function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

export function useNotificationContext() {
    return useContext(NotificationContext);
}

export function useScrollTop(ref) {
    function scrollTop() {
        if (ref && ref.current) {
            // eslint-disable-next-line no-param-reassign
            ref.current.scrollTop = 0;
        }
    }

    return [scrollTop];
}

export function useToggle(initialState) {
    const [toggleValue, setToggleValue] = useState(initialState);
    function toggleFunc(val) {
        if (val || val === false) {
            setToggleValue(val);
        } else {
            setToggleValue(!toggleValue);
        }
    }

    return [toggleValue, toggleFunc];
}

export function useToggleChat(showChat) {
    useEffect(() => {
        const showWidget = showChat ? 'show' : 'hide';

        if (window.zE) {
            window.zE('webWidget', showWidget);
        }
    }, [showChat]);
}

function getWindowDimensions() {
    return {
        height: window.innerHeight,
        isMobile: window.innerWidth < small.max,
        width: window.innerWidth,
    };
}

export function useDimensions() {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    function handleResize() {
        setWindowDimensions(getWindowDimensions());
    }

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => { window.removeEventListener('resize', handleResize); };
    }, []);

    return windowDimensions;
}

export function useIsMobile() {
    const [isMobile, setIsMobile] = useState(window.innerWidth < small.max);

    function handleResize() {
        setIsMobile(window.innerWidth < small.max);
    }

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => { window.removeEventListener('resize', handleResize); };
    }, []);

    return isMobile;
}

export function useStateWithCallback(initialValue) {
    const callbackRef = useRef(null);

    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        if (callbackRef.current) {
            callbackRef.current(value);

            callbackRef.current = null;
        }
    });

    const setValueWithCallback = (newValue, callback) => {
        callbackRef.current = callback;

        return setValue(newValue);
    };

    return [value, setValueWithCallback];
}
